import { Activity } from 'types/Activity'
import { TranslateFunction } from 'types/common'
import { Job } from 'types/Job'
import { getFullName, getProductFamilyName } from 'utils'
import { getActivityGroupedKey } from './getActivityGroupedKey'

export const getJobActivity = (
  job: Job,
  t: TranslateFunction,
  reportTranslations: { [key: string]: string },
): Activity | null => {
  const baseActivity = {
    ...job,
    id: job.id,
    isSuccessful: job.status === 'COMPLETED',
    endTime: job.endTime ? +job.endTime : undefined,
  }
  const activityGroupKeyType = getActivityGroupedKey({ jobType: job.type })

  switch (activityGroupKeyType) {
    case 'setuserlicenses': {
      const userCount = job.metadata?.users?.length
      const products = job.metadata.licenses
        .map((license) => getProductFamilyName(license.description).trim())
        .join(', ')
      const groups = job.metadata.groups?.map((group) => group.name.trim()).join(', ')

      let successMsg = ''
      let errorMsg = ''

      if (userCount) {
        successMsg = t('%{products} added to %{smart_count} user |||| %{products} added to %{smart_count} users', {
          products,
          smart_count: userCount || 0,
        })

        errorMsg = t(
          'Error adding %{products} to %{smart_count} user |||| Error adding %{products} to %{smart_count} users',
          {
            products,
            smart_count: userCount || 0,
          },
        )
      } else if (groups) {
        successMsg = t('%{products} added to users in group(s) %{groups}', {
          products,
          groups,
        })
        errorMsg = t('Error adding %{products} to users in group(s) %{groups}', { products, groups })
      } else {
        successMsg = t('%{products} added to all users', {
          userCount,
          products,
        })
        errorMsg = t('Error adding %{products} to all users', { products })
      }

      return {
        ...baseActivity,
        message: baseActivity.isSuccessful ? successMsg : errorMsg,
      }
    }

    case 'unlicenseusers': {
      const userCount = job.metadata?.users?.length
      const products = job.metadata.licenses.map((license) => getProductFamilyName(license.description)).join(', ')
      const groups = job.metadata.groups?.map((group) => group.name).join(', ')

      let successMsg = ''
      let errorMsg = ''

      if (userCount) {
        successMsg = t(
          '%{products} removed from %{smart_count} user |||| %{products} removed from %{smart_count} users',
          {
            products,
            smart_count: userCount || 0,
          },
        )

        errorMsg = t(
          'Error removing %{products} from %{smart_count} user |||| Error removing %{products} from %{smart_count} users',
          {
            products,
            smart_count: userCount || 0,
          },
        )
      } else if (groups) {
        successMsg = t('%{products} removed from users in group(s) %{groups}', {
          products,
          groups,
        })
        errorMsg = t('Error removing %{products} from users in group(s) %{groups}', { products, groups })
      } else {
        successMsg = t('%{products} removed from all users', {
          products,
        })
        errorMsg = t('Error removing %{products} from all users', { products })
      }

      return {
        ...baseActivity,
        message: baseActivity.isSuccessful ? successMsg : errorMsg,
      }
    }

    case 'groupusers': {
      const userCount = job.metadata?.users?.length
      const newGroup = job.metadata.groups?.[1]?.name || job.metadata.groups?.[0]?.name
      const oldGroup = job.metadata.groups?.[1] ? job.metadata.groups?.[0]?.name : null

      let successMsg = ''
      let errorMsg = ''

      if (userCount) {
        successMsg = t(
          '%{smart_count} user added to group %{newGroup} |||| %{smart_count} users added to group %{newGroup}',
          {
            newGroup,
            smart_count: userCount || 0,
          },
        )

        errorMsg = t(
          'Error adding %{smart_count} user to group %{newGroup} |||| Error adding %{smart_count} users to group %{newGroup}',
          {
            newGroup,
            smart_count: userCount || 0,
          },
        )
      } else if (oldGroup) {
        successMsg = t('Users in group %{oldGroup} added to %{newGroup}', {
          oldGroup,
          newGroup,
        })
        errorMsg = t('Error adding users in group %{oldGroup} to %{newGroup}', {
          oldGroup,
          newGroup,
        })
      } else {
        successMsg = t('All users added to group %{newGroup}', { newGroup })
        errorMsg = t('Error adding all users to group %{newGroup}', { newGroup })
      }

      return {
        ...baseActivity,
        message: baseActivity.isSuccessful ? successMsg : errorMsg,
      }
    }
    case 'ungroupusers': {
      const userCount = job.metadata?.users?.length
      const groups = job.metadata.groups?.map((group) => group.name).join(', ')

      let successMsg = ''
      let errorMsg = ''

      if (userCount) {
        successMsg = t('%{smart_count} user removed from groups |||| %{smart_count} users removed from groups', {
          smart_count: userCount || 0,
        })

        errorMsg = t(
          'Error removing %{smart_count} user from groups |||| Error removing %{smart_count} users from groups',
          {
            smart_count: userCount || 0,
          },
        )
      } else if (groups) {
        successMsg = t('Users in group(s) %{groups} removed from their groups', {
          groups,
        })
        errorMsg = t('Error removing users in group(s) %{groups} from their groups', { groups })
      } else {
        successMsg = t('All users removed from groups')
        errorMsg = t('Error removing all users from groups')
      }

      return {
        ...baseActivity,
        message: baseActivity.isSuccessful ? successMsg : errorMsg,
      }
    }
    case 'adduser':
    case 'addusers': {
      const userAdded = `${JSON.parse(job.response.content)[0]?.email}`
      const successMsg = t('%{userAdded} has been added', { userAdded: userAdded })
      const errorMsg = t('Error while adding %{userAdded}', { userAdded: userAdded })

      return { ...baseActivity, message: baseActivity.isSuccessful ? successMsg : errorMsg }
    }
    case 'report': {
      const successMsg = t('%{reportType} has been created', { reportType: reportTranslations[String(job.type)] })
      const errorMsg = t('Error while creating %{reportType}', { reportType: reportTranslations[String(job.type)] })

      return {
        ...baseActivity,
        message: baseActivity.isSuccessful ? successMsg : errorMsg,
      }
    }
    case 'webinarrecordingregistrantreport': {
      const successMsg = t('%{reportType} has been created', { reportType: reportTranslations[activityGroupKeyType] })
      const errorMsg = t('Error while creating %{reportType}', { reportType: reportTranslations[activityGroupKeyType] })

      return { ...baseActivity, message: baseActivity.isSuccessful ? successMsg : errorMsg }
    }
    case 'deleteuser': {
      const userCount = job.metadata?.users.length

      const successMsg = t('%{smart_count} user deleted |||| %{smart_count} users deleted', {
        smart_count: userCount || 0,
      })

      const errorMsg = t('Error deleting %{smart_count} user |||| Error deleting %{smart_count} users', {
        smart_count: userCount || 0,
      })

      return {
        ...baseActivity,
        message: baseActivity.isSuccessful ? successMsg : errorMsg,
      }
    }
    case 'addtemplate': {
      const templateName = JSON.parse(job.request?.content || '{}').title

      const successMsg = t('Email template %{templateName} was created', {
        templateName,
      })
      const errorMsg = t('Error adding email template')

      return {
        ...baseActivity,
        message: baseActivity.isSuccessful ? successMsg : errorMsg,
      }
    }
    case 'updatetemplate': {
      const templateName = JSON.parse(job.request?.content || '{}').title

      const successMsg = t('Email template %{templateName} was updated', {
        templateName,
      })
      const errorMsg = t('Error updating email template')

      return {
        ...baseActivity,
        message: baseActivity.isSuccessful ? successMsg : errorMsg,
      }
    }
    case 'deletetemplate': {
      const successMsg = t('Email template has been removed')
      const errorMsg =
        job.response?.status === 404 ? t('Email template could not be found') : t('Email template could not be removed')

      return {
        ...baseActivity,
        message: baseActivity.isSuccessful ? successMsg : errorMsg,
      }
    }
    case 'addattribute': {
      const attributeName = JSON.parse(job.request?.content || '{}').name.toLowerCase()
      let successMsg = ''

      if (attributeName !== 'title' && attributeName !== 'location') {
        successMsg = t('%{attribute_name} has been added', { attribute_name: attributeName })
      }
      const errorMsg =
        job.response?.status === 409
          ? t('An attribute already has the same name')
          : t('Error: The attribute was not added')
      return {
        ...baseActivity,
        message: baseActivity.isSuccessful ? successMsg : errorMsg,
      }
    }
    case 'updateattribute': {
      const attributeName = JSON.parse(job.request?.content || '{}').name
      const successMsg = t('%{attribute_name} has been added', { attribute_name: attributeName })
      const errorMsg =
        job.response?.status === 409
          ? t('An attribute already has the same name')
          : t('Error: The attribute was not added')
      return {
        ...baseActivity,
        message: baseActivity.isSuccessful ? successMsg : errorMsg,
      }
    }

    case 'addgroup': {
      const successMsg = t('User group added')
      const errorMsg = t('Error adding user group')

      return {
        ...baseActivity,
        message: baseActivity.isSuccessful ? successMsg : errorMsg,
      }
    }

    case 'deletegroup': {
      const groupCount = job.metadata?.groups?.length || 0

      const successMsg =
        groupCount > 1
          ? t('%{groupCount} user groups deleted', {
              groupCount,
            })
          : t('User group deleted')

      const errorMsg = groupCount > 1 ? t('Error deleting user groups') : t('Error deleting user group')

      return {
        ...baseActivity,
        message: baseActivity.isSuccessful ? successMsg : errorMsg,
      }
    }
    // profiles (settings templates)
    case 'createprofiles': {
      const templateAdded = `${JSON.parse(job?.request?.content)?.name}`
      const successMsg = t('%{template} succefully created', { template: templateAdded })
      const errorMsg = t('Error while creating %{template}', { template: templateAdded })

      return { ...baseActivity, message: baseActivity.isSuccessful ? successMsg : errorMsg }
    }
    case 'deleteprofile': {
      const templateDeleted = job?.metadata?.profiles[0]?.name
      const successMsg = t('%{template} succefully deleted', { template: templateDeleted })
      const errorMsg = t('Error while deleting %{template}', { template: templateDeleted })

      return { ...baseActivity, message: baseActivity.isSuccessful ? successMsg : errorMsg }
    }
    case 'updateprofileg2wsettings': {
      const templateUpdated = job?.metadata?.profiles[0]?.name
      const successMsg = t('%{template} webinar settings updated', { template: templateUpdated })
      const errorMsg = t('Error while updating webinar settings of %{template}', { template: templateUpdated })

      return { ...baseActivity, message: baseActivity.isSuccessful ? successMsg : errorMsg }
    }
    case 'updateprofileg2tsettings': {
      const templateUpdated = job?.metadata?.profiles[0]?.name
      const successMsg = t('%{template} training settings updated', { template: templateUpdated })
      const errorMsg = t('Error while updating training settings of %{template}', { template: templateUpdated })

      return { ...baseActivity, message: baseActivity.isSuccessful ? successMsg : errorMsg }
    }
    case 'updateprofileg2msettings': {
      const templateUpdated = job?.metadata?.profiles[0]?.name
      const successMsg = t('%{template} meeting settings updated', { template: templateUpdated })
      const errorMsg = t('Error while updating meeting settings of %{template}', { template: templateUpdated })

      return { ...baseActivity, message: baseActivity.isSuccessful ? successMsg : errorMsg }
    }
    case 'updateprofileg2arssettings': {
      const templateUpdated = job?.metadata?.profiles[0]?.name
      const successMsg = t('%{template} assist settings updated', { template: templateUpdated })
      const errorMsg = t('Error while updating assist settings of %{template}', { template: templateUpdated })

      return { ...baseActivity, message: baseActivity.isSuccessful ? successMsg : errorMsg }
    }
    case 'updateprofilename': {
      const templateUpdated = job?.metadata?.profiles[0]?.name
      const successMsg = t("%{template}'s name successfully updated", { template: templateUpdated })
      const errorMsg = t('Error while updating %{template} name', { template: templateUpdated })

      return { ...baseActivity, message: baseActivity.isSuccessful ? successMsg : errorMsg }
    }

    case 'setprofilesettingstousers': {
      const templateApplied = job?.metadata?.profiles ? job?.metadata?.profiles[0]?.name : 'default'
      const user = job?.metadata?.users[0]?.firstName.concat(' ', job?.metadata?.users[0]?.lastName)

      const successMsg = t('%{template} template applied to %{user}', { template: templateApplied, user: user })
      const errorMsg = t('Error while appling %{template} template', { template: templateApplied })

      return { ...baseActivity, message: baseActivity.isSuccessful ? successMsg : errorMsg }
    }
    case 'unlicenseconcurrentusers': {
      const user = job?.metadata?.users[0]?.firstName.concat(' ', job?.metadata?.users[0]?.lastName)

      const successMsg = t('Session with %{user} ended', { user })
      const errorMsg = t('Error while ending session')

      return { ...baseActivity, message: baseActivity.isSuccessful ? successMsg : errorMsg }
    }

    case 'updateprofilejivesettings': {
      const templateUpdated = job?.metadata?.profiles[0]?.name
      const successMsg = t('%{template} phones settings updated', { template: templateUpdated })
      const errorMsg = t('Error while updating phones settings of %{template}', { template: templateUpdated })

      return { ...baseActivity, message: baseActivity.isSuccessful ? successMsg : errorMsg }
    }

    case 'updatedefaultprofilejivesettings': {
      const successMsg = t('Successfully updated the phones settings from default template')
      const errorMsg = t('Failed to update the default phones settings template')

      return { ...baseActivity, message: baseActivity.isSuccessful ? successMsg : errorMsg }
    }

    case 'updateallusersjivesettings': {
      const successMsg = t('User phone settings successfully updated')
      const errorMsg = t('Error while updating user phone settings')

      return { ...baseActivity, message: baseActivity.isSuccessful ? successMsg : errorMsg }
    }

    case 'licenseallusers': {
      return {
        ...baseActivity,
        message: t('License updated'),
      }
    }

    case 'updategroup': {
      return {
        ...baseActivity,
        message: t('Update group'),
      }
    }
    case 'reassignmeetings': {
      return {
        ...baseActivity,
        message: t('Reassign meetings'),
      }
    }
    case 'resendwelcomeemails': {
      return {
        ...baseActivity,
        message: t('Resend welcome emails'),
      }
    }
    case 'allactivities': {
      return {
        ...baseActivity,
        message: t('All activities'),
      }
    }
    case 'commonactivities': {
      return {
        ...baseActivity,
        message: t('Common activities'),
      }
    }
    case 'resendwelcomeemail': {
      return {
        ...baseActivity,
        message: t('Resend welcome email'),
      }
    }

    case 'updateuserdetails': {
      let successMsg = t('Successfully updated user details')
      let errorMsg = t('Error while updating user details')

      const requestContent = JSON.parse(job.request?.content || '{}')
      const responseContent = JSON.parse(job.response?.content || '{}')
      const errorCode = responseContent?.errors?.[0]?.code

      if (requestContent?.firstName && requestContent?.lastName) {
        const name = getFullName(requestContent)

        successMsg = t('Successfully updated the details of %{name}', { name })
        errorMsg = t('Error while updating the details of %{name}', { name })
      }

      if (errorCode === 'user.email.conflict') {
        errorMsg = t('Error while updating user details, the entered email already exist in our systems')
      }

      return {
        ...baseActivity,
        message: baseActivity.isSuccessful ? successMsg : errorMsg,
      }
    }
    case 'addscheduledreport': {
      const reportName = JSON.parse(job.request?.content || '{}').name

      const successMsg = t('New scheduled report %{reportName} successfully created', {
        reportName,
      })
      const errorMsg = t('Error while creating the scheduled report')

      return {
        ...baseActivity,
        message: baseActivity.isSuccessful ? successMsg : errorMsg,
      }
    }
    case 'updatescheduledreport': {
      const reportName = JSON.parse(job.request?.content || '{}').name

      const successMsg = t('Scheduled report %{reportName} successfully updated', {
        reportName,
      })
      const errorMsg = t('Error while updating the scheduled report')

      return {
        ...baseActivity,
        message: baseActivity.isSuccessful ? successMsg : errorMsg,
      }
    }
    case 'deletescheduledreport': {
      const successMsg = t('Scheduled report deleted successfully')
      const errorMsg = t('Error while deleting the scheduled report')

      return {
        ...baseActivity,
        message: baseActivity.isSuccessful ? successMsg : errorMsg,
      }
    }

    case 'updateproducts': {
      return {
        ...baseActivity,
        message: t('Update products'),
      }
    }
    case 'removeproducts': {
      return {
        ...baseActivity,
        message: t('Remove products'),
      }
    }
    case 'updatesettings': {
      return {
        ...baseActivity,
        message: t('Update settings'),
      }
    }
    default:
      return null
  }
}
